import { Component, inject, Input, OnInit, ViewChild } from '@angular/core';
import { Ride } from '../../../../models/ride';
import { CommonModule, DatePipe } from '@angular/common';
import { DurationPipe } from '../../../../@pipes/duration-pipe';
import { ShowLocationDialog } from '../../../../@shared/dialogs/show-location-dialog/show-location-dialog';
import { RideTrajectoryForm } from '../../../../@shared/forms/rides/ride-trajectory-form/ride-trajectory-form';
import { CapitalizePipe } from '../../../../@pipes/capitalize-pipe';
import { Router, RouterLink } from '@angular/router';
import { GoogleMap, GoogleMapsModule } from '@angular/google-maps';
import { RideService } from '../../../../services/rides/ride-service';
import { GREEN_ZONES, RED_ZONES } from '../../../../constants/zones.constants';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-ride-details-general-info',
  imports: [CommonModule, DatePipe, DurationPipe, CapitalizePipe, ShowLocationDialog, RideTrajectoryForm, GoogleMapsModule, DialogModule, RouterLink],
  templateUrl: './ride-details-general-info.html',
  styleUrl: './ride-details-general-info.scss'
})
export class RideDetailsGeneralInfo implements OnInit {

  @Input() rideDetails: Ride

  @ViewChild('inlineMapRef') inlineMapRef!: GoogleMap;

  private rideService = inject(RideService)
  private router = inject(Router)

  endLocationDialog = false
  rideTrajectoryDialog = false
  photoIndex = 0
  lightboxVisible = false
  lightboxIndex = 0

  // ── Trajectory ────────────────────────────────────────────
  mapCenter: google.maps.LatLngLiteral = { lat: 25.2048, lng: 55.2708 }
  startPoint?: google.maps.LatLngLiteral
  endPoint?: google.maps.LatLngLiteral
  curvedPath: google.maps.LatLngLiteral[] = []
  trajectoryLoaded = false
  hasTrajectory = false
  google = google
  private zonePolygons: google.maps.Polygon[] = []

  ngOnInit() {
    this.loadTrajectory()
  }

  loadTrajectory() {
    if (!this.rideDetails?.rideId) return
    this.rideService.getRideTrajectory(this.rideDetails.rideId).subscribe({
      next: (res) => {
        const valid = (res.data ?? []).filter(p =>
          typeof p.latitude === 'number' && typeof p.longitude === 'number'
        )
        this.trajectoryLoaded = true
        if (!valid.length) { this.hasTrajectory = false; return }

        this.hasTrajectory = true
        this.startPoint = { lat: valid[0].latitude, lng: valid[0].longitude }
        this.endPoint = { lat: valid[valid.length - 1].latitude, lng: valid[valid.length - 1].longitude }
        this.mapCenter = this.startPoint
        this.curvedPath = this.buildArc(this.startPoint, this.endPoint)

        setTimeout(() => this.fitBounds(), 600)
      },
      error: () => { this.trajectoryLoaded = true; this.hasTrajectory = false }
    })
  }

  fitBounds() {
    const map = this.inlineMapRef?.googleMap
    if (!map || !this.startPoint || !this.endPoint) {
      setTimeout(() => this.fitBounds(), 400)
      return
    }
    google.maps.event.trigger(map, 'resize')
    const bounds = new google.maps.LatLngBounds()
    bounds.extend(this.startPoint)
    bounds.extend(this.endPoint)
    map.fitBounds(bounds, 20)
  }

  private buildArc(
    p0: google.maps.LatLngLiteral,
    p2: google.maps.LatLngLiteral,
    steps = 40,
    curvature = 0.2
  ): google.maps.LatLngLiteral[] {
    // midpoint
    const mx = (p0.lat + p2.lat) / 2;
    const my = (p0.lng + p2.lng) / 2;
    // perpendicular offset proportional to distance
    const dlat = p2.lat - p0.lat;
    const dlng = p2.lng - p0.lng;
    // control point: midpoint shifted perpendicular to the line
    const p1: google.maps.LatLngLiteral = {
      lat: mx - dlng * curvature,
      lng: my + dlat * curvature,
    };
    const pts: google.maps.LatLngLiteral[] = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const u = 1 - t;
      pts.push({
        lat: u * u * p0.lat + 2 * u * t * p1.lat + t * t * p2.lat,
        lng: u * u * p0.lng + 2 * u * t * p1.lng + t * t * p2.lng,
      });
    }
    return pts;
  }

  onMapReady(map: google.maps.Map) {
    GREEN_ZONES.forEach(points => {
      this.zonePolygons.push(new google.maps.Polygon({
        paths: points, map,
        strokeColor: '#2e7d32', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#4caf50', fillOpacity: 0.18, clickable: false, zIndex: 1,
      }));
    });
    RED_ZONES.forEach(points => {
      this.zonePolygons.push(new google.maps.Polygon({
        paths: points, map,
        strokeColor: '#c62828', strokeOpacity: 0.9, strokeWeight: 2,
        fillColor: '#f44336', fillOpacity: 0.18, clickable: false, zIndex: 1,
      }));
    });
  }

  get photos(): string[] {
    const p = this.rideDetails?.endRidePhotos
    if (!p) return []
    return [p.parkingBoardPhotoUrl]
      .filter(Boolean) as string[]
  }

  prevPhoto = () => {
    this.photoIndex = (this.photoIndex - 1 + this.photos.length) % this.photos.length
  }

  nextPhoto = () => {
    this.photoIndex = (this.photoIndex + 1) % this.photos.length
  }

  openLightbox = (index: number) => {
    this.lightboxIndex = index;
    this.lightboxVisible = true;
  }

  lightboxPrev = () => {
    this.lightboxIndex = (this.lightboxIndex - 1 + this.photos.length) % this.photos.length;
  }

  lightboxNext = () => {
    this.lightboxIndex = (this.lightboxIndex + 1) % this.photos.length;
  }

  openEndLocationDialog = () => {
    this.endLocationDialog = true
  }

  openRideTrajectoryDialog = () => {
    this.rideTrajectoryDialog = true
  }

  getStatusClass = (status?: string): string => {
    switch (status) {
      case 'ACTIVE':
        return 'text-green-500';
      case 'COMPLETED':
        return 'text-blue-500';
      case 'COMPLETED_WITH_DEBT':
        return 'text-red-700';
      default:
        return '';
    }
  }

  openEndLocation = () => {
    const lat = this.rideDetails.telemetryEndLat;
    const lng = this.rideDetails.telemetryEndLng;

    if (lat && lng) {
      const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
      window.open(url, '_blank');
    }
  }

  redirectToVehicleDetails = (carId: number) => {
    this.router.navigate([`/vehicles/${carId}`]);
  }

  redirectToCustomerDetails = (customerId: number) => {
    this.router.navigate([`/customers/${customerId}`]);
  }

  onVehicleClick(event: MouseEvent, carId: number) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.redirectToVehicleDetails(carId);
  }

  onCustomerClick(event: MouseEvent, customerId: number) {
    if (event.ctrlKey || event.metaKey) return;
    event.preventDefault();
    this.redirectToCustomerDetails(customerId);
  }

  goToVehiclePage = (search: string) => {
    this.router.navigate(['/vehicles'], {
      queryParams: { search },
      queryParamsHandling: ''
    });
  }

  goToCustomersPage = (search: string) => {
    this.router.navigate(['/customers'], {
      queryParams: { search },
      queryParamsHandling: ''
    });
  }
}
